import { LEARNING_PHASES } from "../constants/learningPhases";

/** Middle of the 15–30s auto-save window. */
export const TRAINING_PROGRESS_SAVE_MS = 20000;

export const TRAINING_PROGRESS_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const TRAINING_PROGRESS_VERSION = 1;
const KEY_PREFIX = "ld_training_progress";

/** In-progress and retryable only. A pass or lock stays a server result. */
const RESUMABLE_PHASES = [
  LEARNING_PHASES.GUIDANCE,
  LEARNING_PHASES.QUIZ,
  LEARNING_PHASES.FAIL,
];

function storage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch (e) {
    return null;
  }
}

/** Same invite, package, and learner across a resent training email. */
export function trainingProgressKey({ packageId, userId, inviteId } = {}) {
  if (!packageId) {
    return null;
  }

  return [KEY_PREFIX, packageId, userId || "", inviteId || ""].join(":");
}

export function shouldRestoreTrainingProgress(phase) {
  return phase !== LEARNING_PHASES.SUCCESS && phase !== LEARNING_PHASES.LOCKED;
}

function pickAnswers(answeres) {
  const saved = {};

  Object.entries(answeres || {}).forEach(([id, answer]) => {
    if (!answer || typeof answer !== "object" || answer.value === undefined) {
      return;
    }

    saved[id] = { value: answer.value, type: answer.type };
    if (answer.score_exclude !== undefined) {
      saved[id].score_exclude = answer.score_exclude;
    }
  });

  return saved;
}

/** Null on the intro screen so a reload cannot wipe a real session. */
export function buildTrainingSnapshot(learning, answers) {
  const phase = learning?.phase;
  if (!RESUMABLE_PHASES.includes(phase)) {
    return null;
  }

  const guidance = learning.guidance || {};

  return {
    v: TRAINING_PROGRESS_VERSION,
    savedAt: new Date().toISOString(),
    phase,
    guidanceIndex: guidance.index || 0,
    documentReadPercent: guidance.documentReadPercent || 0,
    documentComplete: Boolean(guidance.documentComplete),
    documentNeedsScroll: Boolean(guidance.documentNeedsScroll),
    questionIndex: learning.questionnaire?.questionIndex || 0,
    answers: pickAnswers(answers),
    quizResult: learning.quizResult || null,
  };
}

export function readTrainingProgress(key, stepCount = 0) {
  const ls = storage();
  if (!ls || !key) {
    return null;
  }

  try {
    const raw = ls.getItem(key);
    if (!raw) {
      return null;
    }

    const data = JSON.parse(raw);
    const savedAt = data?.savedAt ? new Date(data.savedAt).getTime() : NaN;
    if (
      !data
      || data.v !== TRAINING_PROGRESS_VERSION
      || !RESUMABLE_PHASES.includes(data.phase)
      || !Number.isFinite(savedAt)
      || Date.now() - savedAt > TRAINING_PROGRESS_TTL_MS
    ) {
      ls.removeItem(key);
      return null;
    }

    const guidanceIndex = Math.max(0, Number(data.guidanceIndex) || 0);
    const maxIndex = stepCount > 0 ? stepCount - 1 : guidanceIndex;

    return {
      ...data,
      guidanceIndex: Math.min(guidanceIndex, maxIndex),
      questionIndex: Math.max(0, Number(data.questionIndex) || 0),
      answers: data.answers && typeof data.answers === "object" ? data.answers : {},
    };
  } catch (e) {
    return null;
  }
}

export function writeTrainingProgress(key, snapshot) {
  const ls = storage();
  if (!ls || !key || !snapshot) {
    return;
  }

  try {
    ls.setItem(key, JSON.stringify(snapshot));
  } catch (e) {
    // Storage full or blocked: the in-memory session still works.
  }
}

/** Drop a stored session once the server owns the outcome. */
export function removeTrainingProgress(key) {
  const ls = storage();
  if (!ls || !key) {
    return;
  }

  try {
    ls.removeItem(key);
  } catch (e) {
    // Storage blocked: the server result still wins on the next load.
  }
}
